import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Code,
    GitPullRequest,
    Clock,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    Users,
    Database,
} from 'lucide-react';
import { fetchDora, fetchPRSummary } from '../data/adapters';
import { Dora, PRSummary } from '../types';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const CodeOpsTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [doraData, setDoraData] = useState<Dora | null>(null);
    const [prData, setPrData] = useState<PRSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const [dora, prs] = await Promise.all([
                    fetchDora(isDemo).catch(() => null),
                    fetchPRSummary(isDemo).catch(() => null),
                ]);
                setDoraData(dora);
                setPrData(prs);
                if (!dora && !prs) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
                {/* CodeOps Metrics Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    const renderNoDataPlaceholder = (title: string) => (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Database size={32} className="mb-2 opacity-50" />
            <p className="text-sm">{title}</p>
            <p className="text-xs mt-1">No data available</p>
        </div>
    );

    if (noData || (!doraData && !prData)) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">
                                Deployment Frequency
                            </h3>
                            <Code size={20} className="text-blue-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--/day</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Lead Time</h3>
                            <Clock size={20} className="text-green-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--h</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">MTTR</h3>
                            <TrendingUp size={20} className="text-purple-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--h</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">
                                Change Failure Rate
                            </h3>
                            <AlertTriangle size={20} className="text-orange-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Pull Request Overview</h3>
                    {renderNoDataPlaceholder('PR data')}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">
                            PR Aging Distribution
                        </h3>
                        {renderNoDataPlaceholder('PR aging data')}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">At-Risk PRs</h3>
                        {renderNoDataPlaceholder('At-risk PR data')}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">
                        DORA Performance Insights
                    </h3>
                    {renderNoDataPlaceholder('DORA metrics')}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                    {renderNoDataPlaceholder('Recommendations')}
                </motion.div>
            </div>
        );
    }

    if (!doraData || !prData) return null;

    return (
        <div className="space-y-6">
            {/* DORA Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Deployment Frequency */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Deployment Frequency</h3>
                        <Code size={20} className="text-blue-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {doraData.deployFreqPerDay}/day
                    </div>
                    <p className="text-sm text-slate-400">Average deployments per day</p>
                </motion.div>

                {/* Lead Time for Changes */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Lead Time</h3>
                        <Clock size={20} className="text-green-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {doraData.leadTimeHoursP50}h
                    </div>
                    <p className="text-sm text-slate-400">Median commit to deploy</p>
                </motion.div>

                {/* MTTR */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">MTTR</h3>
                        <TrendingUp size={20} className="text-purple-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {doraData.mttrHoursP50}h
                    </div>
                    <p className="text-sm text-slate-400">Mean time to recovery</p>
                </motion.div>

                {/* Change Failure Rate */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Change Failure Rate</h3>
                        <AlertTriangle size={20} className="text-orange-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {doraData.changeFailureRatePct}%
                    </div>
                    <p className="text-sm text-slate-400">Failed deployments</p>
                </motion.div>
            </div>

            {/* PR Summary */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Pull Request Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                        <div className="text-3xl font-bold text-white mb-2">{prData.openCount}</div>
                        <div className="text-sm text-slate-400">Open PRs</div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl font-bold text-white mb-2">
                            {prData.medianReviewHours}h
                        </div>
                        <div className="text-sm text-slate-400">Median Review Time</div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl font-bold text-white mb-2">
                            {prData.aging.reduce((sum, a) => sum + a.count, 0)}
                        </div>
                        <div className="text-sm text-slate-400">Total Aging PRs</div>
                    </div>
                </div>
            </motion.div>

            {/* PR Aging & At-Risk */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* PR Aging */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">PR Aging Distribution</h3>
                    <div className="space-y-4">
                        {prData.aging.map(bucket => (
                            <div
                                key={bucket.bucket}
                                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                            >
                                <div className="flex items-center space-x-3">
                                    <div
                                        className={`w-3 h-3 rounded-full ${
                                            bucket.bucket === '0-1d'
                                                ? 'bg-green-400'
                                                : bucket.bucket === '1-3d'
                                                  ? 'bg-yellow-400'
                                                  : bucket.bucket === '3-7d'
                                                    ? 'bg-orange-400'
                                                    : 'bg-red-400'
                                        }`}
                                    />
                                    <span className="text-slate-300">{bucket.bucket}</span>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-semibold text-white">
                                        {bucket.count}
                                    </div>
                                    <div className="text-sm text-slate-400">PRs</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* At-Risk PRs */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">At-Risk PRs</h3>
                    <div className="space-y-4">
                        {prData.topAtRisk.map(pr => (
                            <div
                                key={`${pr.repo}-${pr.pr}`}
                                className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50"
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className="font-mono text-sm text-blue-400">
                                                {pr.repo}
                                            </span>
                                            <span className="text-sm text-slate-400">#{pr.pr}</span>
                                        </div>
                                        <h4 className="text-sm font-medium text-white line-clamp-2">
                                            {pr.title}
                                        </h4>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-semibold text-red-400">
                                            {pr.daysOpen}d
                                        </div>
                                        <div className="text-xs text-slate-400">open</div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between text-xs text-slate-400">
                                    <span>Reviewers: {pr.reviewersPending.join(', ')}</span>
                                    <span className="text-yellow-400">At Risk</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>

            {/* DORA Performance Insights */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">DORA Performance Insights</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                        <div className="text-sm text-slate-400 mb-2">Deployment Frequency</div>
                        <div
                            className={`text-lg font-semibold ${
                                doraData.deployFreqPerDay >= 1
                                    ? 'text-green-400'
                                    : 'text-yellow-400'
                            }`}
                        >
                            {doraData.deployFreqPerDay >= 1 ? 'Elite' : 'High'}
                        </div>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                        <div className="text-sm text-slate-400 mb-2">Lead Time</div>
                        <div
                            className={`text-lg font-semibold ${
                                doraData.leadTimeHoursP50 <= 1
                                    ? 'text-green-400'
                                    : doraData.leadTimeHoursP50 <= 24
                                      ? 'text-yellow-400'
                                      : 'text-red-400'
                            }`}
                        >
                            {doraData.leadTimeHoursP50 <= 1
                                ? 'Elite'
                                : doraData.leadTimeHoursP50 <= 24
                                  ? 'High'
                                  : 'Medium'}
                        </div>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                        <div className="text-sm text-slate-400 mb-2">MTTR</div>
                        <div
                            className={`text-lg font-semibold ${
                                doraData.mttrHoursP50 <= 1
                                    ? 'text-green-400'
                                    : doraData.mttrHoursP50 <= 24
                                      ? 'text-yellow-400'
                                      : 'text-red-400'
                            }`}
                        >
                            {doraData.mttrHoursP50 <= 1
                                ? 'Elite'
                                : doraData.mttrHoursP50 <= 24
                                  ? 'High'
                                  : 'Medium'}
                        </div>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                        <div className="text-sm text-slate-400 mb-2">Change Failure Rate</div>
                        <div
                            className={`text-lg font-semibold ${
                                doraData.changeFailureRatePct <= 5
                                    ? 'text-green-400'
                                    : doraData.changeFailureRatePct <= 15
                                      ? 'text-yellow-400'
                                      : 'text-red-400'
                            }`}
                        >
                            {doraData.changeFailureRatePct <= 5
                                ? 'Elite'
                                : doraData.changeFailureRatePct <= 15
                                  ? 'High'
                                  : 'Medium'}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Recommendations */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Recommendations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {doraData.changeFailureRatePct > 15 && (
                        <div className="flex items-start space-x-3 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                            <AlertTriangle size={16} className="text-red-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-red-300">
                                    High Change Failure Rate
                                </h4>
                                <p className="text-sm text-red-200">
                                    {doraData.changeFailureRatePct}% failure rate indicates
                                    deployment quality issues.
                                </p>
                            </div>
                        </div>
                    )}

                    {doraData.leadTimeHoursP50 > 24 && (
                        <div className="flex items-start space-x-3 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                            <Clock size={16} className="text-yellow-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-yellow-300">Slow Lead Time</h4>
                                <p className="text-sm text-yellow-200">
                                    {doraData.leadTimeHoursP50}h lead time suggests process
                                    bottlenecks.
                                </p>
                            </div>
                        </div>
                    )}

                    {prData.aging.some(a => a.bucket === '>7d' && a.count > 0) && (
                        <div className="flex items-start space-x-3 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg">
                            <GitPullRequest size={16} className="text-orange-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-orange-300">Aging Pull Requests</h4>
                                <p className="text-sm text-orange-200">
                                    PRs aging beyond 7 days require immediate attention.
                                </p>
                            </div>
                        </div>
                    )}

                    {prData.medianReviewHours > 24 && (
                        <div className="flex items-start space-x-3 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                            <Users size={16} className="text-blue-400 mt-0.5" />
                            <div>
                                <h4 className="font-medium text-blue-300">Slow Review Process</h4>
                                <p className="text-sm text-blue-200">
                                    {prData.medianReviewHours}h median review time suggests review
                                    capacity issues.
                                </p>
                            </div>
                        </div>
                    )}

                    {doraData.changeFailureRatePct <= 15 &&
                        doraData.leadTimeHoursP50 <= 24 &&
                        prData.aging.every(a => a.bucket !== '>7d' || a.count === 0) && (
                            <div className="flex items-start space-x-3 p-3 bg-green-500/20 border border-green-500/30 rounded-lg md:col-span-2">
                                <CheckCircle size={16} className="text-green-400 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-green-300">
                                        Excellent CodeOps Performance
                                    </h4>
                                    <p className="text-sm text-green-200">
                                        All CodeOps metrics are within acceptable ranges. Continue
                                        monitoring for any degradation.
                                    </p>
                                </div>
                            </div>
                        )}
                </div>
            </motion.div>
        </div>
    );
};
